import { useMemo } from 'react'
import { env } from 'process'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { t } from '@lingui/core/macro'

import type { BrandKey } from '@jotta/config/brand'
import { useGetConfig } from '@jotta/grpc-connect-client/config'
import type { BrandingInfo } from '@jotta/grpc-connect-openapi/config'
import type { ConsentRequest } from '@jotta/grpc-connect-openapi/consent'
import { ConsentRequestSchema } from '@jotta/grpc-connect-openapi/consent'
import { Feature } from '@jotta/grpc-connect-openapi/feature'
import type { ProtoCookie } from '@jotta/hooks/persistProtoCookie'
import { persistProtoCookie } from '@jotta/hooks/persistProtoCookie'
import type { MessageData } from '@jotta/types/compat'
import { objectKeys } from '@jotta/types/TypeUtils'
import { useBrandCode, useBrandTheme } from '@jotta/ui/useBrandTheme'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import type { CookieDeclaration } from './CookieDeclaration'

export type Category = 'necessary' | 'functional' | 'statistic' | 'marketing'

export const categoryMap: Record<
  Category,
  | 'necessaryCookieConsent'
  | 'functionalCookieConsent'
  | 'statisticalCookieConsent'
  | 'marketingCookieConsent'
> = {
  necessary: 'necessaryCookieConsent',
  functional: 'functionalCookieConsent',
  statistic: 'statisticalCookieConsent',
  marketing: 'marketingCookieConsent',
}
export type CookieCategory = keyof typeof categoryMap

// TODO e2e tests should scan to verify:
// - domain
// - expiration
export type CookieDeclaration = {
  category: CookieCategory
  data_processor_name: string
  expiration: string
  domain: string
  description: string
  data_processor_privacy_policy: string
  name: string
  regex?: RegExp
}

export type Consent = MessageData<ConsentRequest>

export const categories = objectKeys(categoryMap)

const ownDomain = window.location.hostname
const ownDomainWide = `.${ownDomain}`

function ourCookie(
  value: Pick<
    CookieDeclaration,
    'category' | 'expiration' | 'description' | 'name'
  >,
  brandingInfo: BrandingInfo,
) {
  return {
    ...value,
    data_processor_name: 'Jottacloud',
    data_processor_privacy_policy:
      brandingInfo.privacyLink || 'https://www.jottacloud.com/en/privacy.html',
    domain: ownDomain,
  }
}

const consentCookieName = 'cc'
function consentCookie(brandingInfo: BrandingInfo): CookieDeclaration {
  return ourCookie(
    {
      category: 'necessary',
      expiration: 'a year',
      description:
        'This cookie records your consent preferences regarding the use of cookies on our platform. It ensures that your choices are respected across visits, preventing repeated consent prompts and helping us comply with privacy regulations.',
      name: consentCookieName,
    },
    brandingInfo,
  )
}

function baseCookies(brandingInfo: BrandingInfo): CookieDeclaration[] {
  const loginCookieDescription = t`This cookie is used to secure our login process.`
  return (
    [
      {
        category: 'necessary',
        name: 'lang',
        expiration: t`a year`,
        description: t`We store language preference cookies to remember your selected language settings. This ensures a consistent and localized experience every time you visit our platform.`,
      },
      {
        category: 'necessary',
        name: 'access_token',
        expiration: t`one month`,
        description: t`This cookie stores a token that is required for authenticating the user. It allows the user to interact securely with Jottacloud services.`,
      },
      {
        category: 'necessary',
        name: 'refresh_token',
        expiration: t`one month`,
        description: t`This cookie stores a refresh token used to maintain authentication beyond the lifespan of an access token. It helps keep users signed in by obtaining a new access token without requiring them to log in again.`,
      },
      {
        category: 'necessary',
        name: 'ory_hydra_login_csrf_xxx',
        regex: /ory_hydra_login_csrf_.*/,
        expiration: t`session`,
        description: loginCookieDescription,
      },
      {
        category: 'necessary',
        name: 'ory_hydra_consent_csrf_xxx',
        regex: /ory_hydra_consent_csrf_.*/,
        expiration: t`session`,
        description: loginCookieDescription,
      },
      {
        category: 'necessary',
        name: 'ory_hydra_session',
        expiration: t`session`,
        description: loginCookieDescription,
      },
      {
        category: 'necessary',
        name: 'login_state_xxx',
        regex: /login_state_.*/,
        expiration: t`session`,
        description: loginCookieDescription,
      },
    ] as const
  ).map(value => ourCookie(value, brandingInfo))
}

function stripeCookies(): CookieDeclaration[] {
  return [
    {
      category: 'necessary',
      name: '__stripe_mid',
      data_processor_name: 'Stripe',
      expiration: t`a year`,
      description:
        'Set for fraud prevention purposes and helps us assess the risk associated with an attempted transaction.',
      data_processor_privacy_policy: 'https://stripe.com/privacy',
      domain: ownDomain,
    },
    {
      category: 'necessary',
      name: '__stripe_sid',
      data_processor_name: 'Stripe',
      expiration: t`30 minutes`,
      description:
        'Set for fraud prevention purposes and helps us assess the risk associated with an attempted transaction.',
      data_processor_privacy_policy: 'https://stripe.com/privacy',
      domain: ownDomain,
    },
  ]
}

function intercomCookies(): CookieDeclaration[] {
  return [
    {
      name: 'intercom-device-id-xxx',
      regex: /intercom-device-id-.*/,
      expiration: t`9 months`,
    },
    {
      name: 'intercom-id-xxx',
      regex: /intercom-id-.*/,
      expiration: t`9 months`,
    },
    {
      name: 'intercom-session-xxx',
      regex: /intercom-session-.*/,
      expiration: t`7 days`,
    },
  ].map(value => ({
    category: 'necessary',
    data_processor_name: 'Intercom',
    description: t`This cookie is used to deliver contextual help articles, user guides, onboarding experiences, real-time support, tracking interactions with help resources and enhance the overall user experience.`,
    data_processor_privacy_policy:
      'https://www.intercom.com/terms-and-policies#privacy',
    domain: ownDomainWide,
    ...value,
  }))
}

function extraCookiesByBrand(
  brandKey: BrandKey,
  brandingInfo: BrandingInfo,
): CookieDeclaration[] {
  switch (brandKey) {
    case 'COMHEM':
    case 'ELGIGANTEN':
    case 'ELKJOP':
    case 'ELKO':
    case 'GET':
    case 'GIGANTTI':
    case 'HALEBOP':
    case 'LETS_GO':
    case 'MEDIAMARKT':
    case 'OBOS':
    case 'ONLIME_WL':
    case 'PHONERO':
    case 'SATURN':
    case 'TDC_NO':
    case 'TELIA_CLOUD':
    case 'TEST':
      return []

    case 'JOTTACLOUD':
      const marketingDescription = t`This cookie help us measure the effectiveness of our advertising campaigns, allowing us to reach relevant audiences.`
      const statisticalDescription = t`Collects information about the users and their activity on the website for analytics and reporting purposes.`
      return [
        ourCookie(
          {
            category: 'necessary',
            name: '_production_session',
            description: t`This is a necessary session cookie used by Craft CMS to maintain user sessions while browsing our website.`,
            expiration: t`session`,
          },
          brandingInfo,
        ),

        // TODO remove and cleanup cookie information cookies after switch
        {
          category: 'necessary',
          name: 'CookieInformationConfig',
          data_processor_name: 'Cookie Information',
          expiration: t`a year`,
          description: "Supports the website's technical functions.",
          data_processor_privacy_policy:
            'https://cookieinformation.com/cookie-and-privacy-policy/',
          domain: ownDomain,
        },
        {
          category: 'necessary',
          name: 'CookieInformationConsent',
          data_processor_name: 'Cookie Information',
          expiration: t`a year`,
          description: "Supports the website's technical functions.",
          data_processor_privacy_policy:
            'https://cookieinformation.com/cookie-and-privacy-policy/',
          domain: ownDomain,
        },
        {
          category: 'necessary',
          name: 'CookieInformationConsent_xxx',
          regex: /CookieInformationConsent_.*/,
          data_processor_name: 'Cookie Information',
          expiration: t`a year`,
          description: 'Used to share consent across domains.',
          data_processor_privacy_policy:
            'https://cookieinformation.com/cookie-and-privacy-policy/',
          domain: 'policy.app.cookieinformation.com',
        },
        // TODO remove and cleanup cookie information cookies after switch

        {
          category: 'statistic',
          name: '_pk_idxxx',
          data_processor_name: 'Matomo',
          expiration: t`permanent`,
          description: statisticalDescription,
          data_processor_privacy_policy: 'https://matomo.org/privacy-policy',
          regex: /_pk_id.*/,
          domain: ownDomainWide,
        },
        {
          category: 'statistic',
          name: '_pk_sesxxx',
          regex: /_pk_ses.*/,
          data_processor_name: 'Matomo',
          expiration: t`30 minutes`,
          description: statisticalDescription,
          data_processor_privacy_policy: 'https://matomo.org/privacy-policy',
          domain: ownDomainWide,
        },
        {
          category: 'statistic',
          name: '_pk_refxxx',
          regex: /_pk_ref.*/,
          data_processor_name: 'Matomo',
          expiration: t`6 months`,
          description: statisticalDescription,
          data_processor_privacy_policy: 'https://matomo.org/privacy-policy',
          domain: ownDomainWide,
        },

        {
          category: 'marketing',
          data_processor_name: 'Google',
          expiration: t`3 months`,
          description: marketingDescription,
          data_processor_privacy_policy: 'https://policies.google.com/privacy',
          name: '_gcl_au',
          domain: ownDomainWide,
        },
        {
          category: 'marketing',
          data_processor_name: 'Google',
          expiration: t`3 months`,
          description: marketingDescription,
          data_processor_privacy_policy: 'https://policies.google.com/privacy',
          name: '_gcl_aw',
          domain: ownDomainWide,
        },
        {
          category: 'marketing',
          data_processor_name: 'Google',
          expiration: t`3 months`,
          description: marketingDescription,
          data_processor_privacy_policy: 'https://policies.google.com/privacy',
          name: '_gcl_gs',
          domain: ownDomainWide,
        },
        {
          category: 'marketing',
          data_processor_name: 'Google Marketing Platform',
          expiration: t`15 minutes`,
          description: marketingDescription,
          data_processor_privacy_policy: 'https://policies.google.com/privacy',
          name: 'test_cookie',
          domain: '.doubleclick.net',
        },
        {
          category: 'marketing',
          data_processor_name: 'Google Marketing Platform',
          expiration: t`a year`,
          description: marketingDescription,
          data_processor_privacy_policy: 'https://policies.google.com/privacy',
          name: 'IDE',
          domain: '.doubleclick.net',
        },

        {
          category: 'marketing',
          data_processor_name: 'Microsoft, Bing',
          expiration: t`a year`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://privacy.microsoft.com/en-us/privacystatement',
          name: '_uetvid',
          domain: ownDomainWide,
        },
        {
          category: 'marketing',
          data_processor_name: 'Microsoft, Bing',
          expiration: t`a year`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://privacy.microsoft.com/en-us/privacystatement',
          name: 'MUID',
          domain: '.bing.com',
        },
        {
          category: 'marketing',
          data_processor_name: 'Microsoft, Bing',
          expiration: t`a year`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://privacy.microsoft.com/en-us/privacystatement',
          name: 'MSPTC',
          domain: '.bing.com',
        },
        {
          category: 'marketing',
          data_processor_name: 'Microsoft, Bing',
          expiration: t`a day`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://privacy.microsoft.com/en-us/privacystatement',
          name: '_uetsid',
          domain: ownDomainWide,
        },

        {
          category: 'marketing',
          data_processor_name: 'Facebook',
          expiration: t`3 months`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://www.facebook.com/privacy/explanation',
          name: '_fbp',
          domain: ownDomainWide,
        },
        {
          category: 'marketing',
          data_processor_name: 'Facebook',
          expiration: t`2 years`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://www.facebook.com/privacy/explanation',
          name: '_fbc',
          domain: ownDomainWide,
        },
        {
          category: 'marketing',
          data_processor_name: 'Reddit',
          expiration: t`3 months`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://www.reddit.com/policies/privacy-policy',
          name: '_rdt_uuid',
          domain: ownDomainWide,
        },
        {
          category: 'marketing',
          data_processor_name: 'Reddit',
          expiration: t`3 months`,
          description: marketingDescription,
          data_processor_privacy_policy:
            'https://www.reddit.com/policies/privacy-policy',
          name: '_rdt_em',
          domain: ownDomainWide,
        },
      ]
    default:
      return exhaustiveGuard(brandKey)
  }
}

export const cookieIgnoreList = [
  'CookieConsent',
  'wideDrawerPreference',
  'viewMode',
  'sortBy',
]

export function categoryTitle(category: CookieCategory) {
  switch (category) {
    case 'necessary':
      return t`Required`
    case 'functional':
      return t`Functional`
    case 'statistic':
      return t`Statistical`
    case 'marketing':
      return t`Marketing`
    default:
      exhaustiveGuard(category)
  }
}
export function categoryDescription(category: CookieCategory) {
  switch (category) {
    case 'necessary':
      return t`Strictly necessary cookies help make a website navigable by activating basic functions such as page navigation and access to secure website areas. Without these cookies, the website would not be able to work properly.`
    case 'functional':
      return t`Functional cookies make it possible to save information that changes the way the website appears or acts. For instance your preferred language or region.`
    case 'statistic':
      return t`Statistical cookies help the website owner understand how visitors interact with the website by collecting and reporting information.`
    case 'marketing':
      return t`Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and interesting to the individual user and thus more valuable for publishers and third-party advertisers.`
    default:
      exhaustiveGuard(category)
  }
}

export function hasConsentableCookies(
  cookieDeclarations?: CookieDeclaration[],
) {
  return cookieDeclarations?.some(value => value.category !== 'necessary')
}

export function useCookieDeclarations(): {
  data?: CookieDeclaration[]
  isPending: boolean
} {
  const brandKey = useBrandCode()
  const { data, isPending } = useGetConfig()
  const activeFeatures = data?.activeFeatures
  const brandingInfo = data?.brandingInfo
  return useMemo(() => {
    if (activeFeatures === undefined || brandingInfo === undefined) {
      return { data: undefined, isPending }
    }
    const result = [
      ...baseCookies(brandingInfo),
      ...(activeFeatures.includes(Feature.INTERCOM) ? intercomCookies() : []),
      ...(activeFeatures.includes(Feature.STRIPE) ? stripeCookies() : []),
      ...extraCookiesByBrand(brandKey, brandingInfo),
    ]

    if (hasConsentableCookies(result)) {
      result.push(consentCookie(brandingInfo))
    }

    return { data: result, isPending }
  }, [activeFeatures, brandingInfo, brandKey])
}

export const useCookieConsentStore = create<
  ProtoCookie<typeof ConsentRequestSchema>
>()(
  devtools(
    persistProtoCookie({
      name: consentCookieName,
      type: ConsentRequestSchema,
      maxAgeSeconds: 3 * 365 * 24 * 60 * 60,
    }),
    {
      name: 'ConsentStore',
      enabled: !env.isProduction,
    },
  ),
)

export function useCookieCategoryConsent(category: Category) {
  return useCookieConsentStore(([consent]) =>
    Boolean(consent && consent[categoryMap[category]]),
  )
}
